import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { api } from 'src/common/utils/api';
import {
  ProposedActionStatus,
  SuitabilityReportDocumentView,
  SuitabilityReportProposedAction,
} from '../chat/types';

type SuitabilityReportProposalResponse = {
  id: string;
  conversationId: string;
  templateId: string;
  templateName: string;
  reportFormat: 'REPORT' | 'LETTER';
  documents: SuitabilityReportDocumentView[];
  status: ProposedActionStatus;
  failureReason: string | null;
  clientId: string | null;
  familyId: string | null;
};

function toSuitabilityReportProposedAction(
  proposal: SuitabilityReportProposalResponse,
): SuitabilityReportProposedAction {
  return {
    kind: 'SUITABILITY_REPORT',
    proposalId: proposal.id,
    templateId: proposal.templateId,
    templateName: proposal.templateName,
    reportFormat: proposal.reportFormat,
    documents: proposal.documents,
    status: proposal.status,
    failureReason: proposal.failureReason,
    clientId: proposal.clientId,
    familyId: proposal.familyId,
  };
}

/**
 * Every proposal in the conversation, decided ones included: a reopened chat rebuilds its cards
 * from this list, so a pending-only read would drop a card the moment it was decided.
 */
export function useSuitabilityReportProposalList(
  conversationId: string | undefined,
  enabled: boolean,
) {
  const { data, isError } = useQuery<SuitabilityReportProposalResponse[]>({
    queryKey: ['conversations', conversationId, 'suitability-report-proposals'],
    enabled: enabled && !!conversationId,
    // Flag-gating means a 404 during rollout; never retry or surface it as an error state.
    retry: false,
  });
  const proposals = isError ? [] : (data ?? []);
  return { suitabilityReportProposals: proposals.map(toSuitabilityReportProposedAction) };
}

export function useSuitabilityReportProposals(conversationId: string) {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation<
    { data: SuitabilityReportProposalResponse },
    Error,
    { proposalId: string; decision: 'APPROVE' | 'DENY' }
  >({
    mutationFn: ({ proposalId, decision }) =>
      api.post<SuitabilityReportProposalResponse>(
        `conversations/${conversationId}/suitability-report-proposals/${proposalId}/decision`,
        { decision },
      ),
    onSuccess: ({ data }) => {
      if (data.status !== 'APPROVED') return;
      if (data.clientId) {
        queryClient.invalidateQueries({ queryKey: ['clients', data.clientId, 'all-documents'] });
      }
      if (data.familyId) {
        queryClient.invalidateQueries({ queryKey: ['families', data.familyId, 'all-documents'] });
      }
    },
    // Invalidate on failure too: a 409/404 means the proposal was decided elsewhere, and the
    // pending list must refresh so an orphan widget disappears rather than staying clickable.
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ['conversations', conversationId, 'suitability-report-proposals'],
      });
    },
  });

  // A 409 means the decision already landed, perhaps from a lost response or another tab, so the
  // card shows what the server holds rather than assuming the proposal expired.
  const fetchSuitabilityReportProposal = useCallback(
    async (proposalId: string): Promise<SuitabilityReportProposedAction | null> => {
      const proposals = await queryClient.fetchQuery<SuitabilityReportProposalResponse[]>({
        queryKey: ['conversations', conversationId, 'suitability-report-proposals'],
        staleTime: 0,
      });
      const found = proposals.find(proposal => proposal.id === proposalId);
      return found ? toSuitabilityReportProposedAction(found) : null;
    },
    [queryClient, conversationId],
  );

  return {
    decideSuitabilityReportProposal: mutateAsync,
    isDecidingSuitabilityReport: isPending,
    fetchSuitabilityReportProposal,
  };
}
